import type { NeedsAttentionItem } from '../Home/map/needsAttention';

/**
 * Rotation for the /monitor TV view: an overview of every shipment, then a
 * spotlight on each Critical / At-risk item from the Needs attention ranking,
 * then back to the overview.
 *
 * The current step is tracked by shipment id rather than by index, because
 * the ranking is rebuilt on every stream flush and can reorder mid-dwell.
 */

// One loop is overview + up to 10 spotlights of 10 s: about 1 min 50 s, so
// every Needs attention item (the ranking itself stops at 10) gets a turn
// while the overview still comes round regularly.
export const MONITOR_OVERVIEW_MS = 10_000;
export const MONITOR_SHIPMENT_MS = 10_000;
export const MONITOR_MAX_SPOTLIGHTS = 10;

export interface RotationState {
  /** null while the overview is on screen. */
  shipmentId: string | null;
  /** Kept so a shipment that leaves the ranking mid-dwell stays on screen. */
  item: NeedsAttentionItem | null;
  /** 0-based queue index when this step began; -1 for the overview or unknown. */
  position: number;
  startedAt: number;
}

export type RotationStep =
  | { kind: 'overview' }
  | {
      kind: 'shipment';
      item: NeedsAttentionItem;
      /** 1-based position in the queue; 0 if it has since left the queue. */
      position: number;
      total: number;
    };

/**
 * `size` items of `list` for the given loop, wrapping at the end, so every
 * item comes round within ceil(length / size) loops.
 */
const pageFor = <T>(list: T[], loop: number, size: number): T[] => {
  if (list.length <= size) return list;
  const start = (loop * size) % list.length;
  const page = list.slice(start, start + size);
  return page.length < size
    ? [...page, ...list.slice(0, size - page.length)]
    : page;
};

/**
 * The shipments spotlighted in loop number `loop`, up to `max`: every
 * Critical item, then a page of the At-risk ones that moves on each loop, so
 * a backlog of 100 at-risk shipments is shown in turns instead of the same
 * ten forever (with `max` or more Critical items, those take turns instead).
 * Slots still free go, in this order, to `arriving` shipments (earliest
 * first), a page of the watch items (in a hazard area or near a delayed
 * airport, worst first; moving on each loop like At-risk), then `pickups`
 * (earliest first), so a quiet account still has something to show. Each
 * shipment appears once, under its first group.
 */
export const buildLoopQueue = (
  items: NeedsAttentionItem[],
  loop: number,
  max: number = MONITOR_MAX_SPOTLIGHTS,
  arriving: NeedsAttentionItem[] = [],
  pickups: NeedsAttentionItem[] = []
): NeedsAttentionItem[] => {
  const critical = items.filter((item) => item.severity === 'critical');
  const atRisk = items.filter((item) => item.severity === 'at_risk');
  if (critical.length >= max) return pageFor(critical, loop, max);
  const queue = [...critical, ...pageFor(atRisk, loop, max - critical.length)];
  // Shipments with an alert belong to it, shown this loop or not.
  const listed = new Set(
    [...critical, ...atRisk].map((item) => String(item.shipmentId))
  );
  const fill = (more: NeedsAttentionItem[]) => {
    more.slice(0, max - queue.length).forEach((item) => {
      queue.push(item);
      listed.add(String(item.shipmentId));
    });
  };
  fill(arriving.filter((item) => !listed.has(String(item.shipmentId))));
  // On watch after Arriving soon: one arriving keeps that group.
  const watch = items.filter(
    (item) => item.severity === 'watch' && !listed.has(String(item.shipmentId))
  );
  fill(pageFor(watch, loop, max - queue.length));
  // Pickups last (a watch page that leaves room shows every watch item, so
  // one on watch is already listed).
  fill(pickups.filter((item) => !listed.has(String(item.shipmentId))));
  return queue;
};

/**
 * How many of `items` (the alerts and watch shipments, which loops page
 * through) this loop's `queue` does not show, in any group: a shipment shown
 * as arriving is not left for later.
 */
export const countLater = (
  items: NeedsAttentionItem[],
  queue: NeedsAttentionItem[]
): number => {
  const shown = new Set(queue.map((item) => String(item.shipmentId)));
  return items.filter((item) => !shown.has(String(item.shipmentId))).length;
};

/** The loop's groups, in order: what the header counts within. */
const GROUP_OF: Record<NeedsAttentionItem['severity'], string> = {
  critical: 'Needs attention',
  at_risk: 'Needs attention',
  arriving: 'Arriving',
  watch: 'On watch',
  pickup: 'Picking up',
};

/** "Needs attention", "Arriving", "On watch" or "Picking up", numbered within its own group. */
export const describeGroup = (
  step: RotationStep,
  queue: NeedsAttentionItem[]
): { label: string; position: number; total: number } | null => {
  if (step.kind !== 'shipment') return null;
  const label = GROUP_OF[step.item.severity];
  const group = queue.filter((item) => GROUP_OF[item.severity] === label);
  const index = group.findIndex(
    (item) => String(item.shipmentId) === String(step.item.shipmentId)
  );
  return {
    label,
    position: index + 1,
    total: group.length,
  };
};

export const overviewState = (now: number): RotationState => ({
  shipmentId: null,
  item: null,
  position: -1,
  startedAt: now,
});

export const stepDurationMs = (state: RotationState): number =>
  state.shipmentId === null ? MONITOR_OVERVIEW_MS : MONITOR_SHIPMENT_MS;

const shipmentState = (
  item: NeedsAttentionItem,
  now: number,
  position: number
): RotationState => ({
  shipmentId: String(item.shipmentId),
  item,
  position,
  startedAt: now,
});

/** The step after `state`: overview → first item → … → last item → overview. */
export const advanceRotation = (
  state: RotationState,
  queue: NeedsAttentionItem[],
  now: number
): RotationState => {
  if (state.shipmentId === null) {
    return queue.length > 0
      ? shipmentState(queue[0], now, 0)
      : overviewState(now);
  }
  const index = queue.findIndex(
    (item) => String(item.shipmentId) === state.shipmentId
  );
  // If the current item left the queue (e.g. its alert resolved), the item
  // that moved into its slot is next, so the rest of the loop is not skipped.
  const nextPosition =
    index >= 0 ? index + 1 : state.position >= 0 ? state.position : -1;
  const next = nextPosition >= 0 ? queue[nextPosition] : undefined;
  return next ? shipmentState(next, now, nextPosition) : overviewState(now);
};

/** Manual selection (a click on the rail) restarts the dwell on that item. */
export const jumpToShipment = (
  item: NeedsAttentionItem,
  now: number,
  position = -1
): RotationState => shipmentState(item, now, position);

export const describeStep = (
  state: RotationState,
  queue: NeedsAttentionItem[]
): RotationStep => {
  if (state.shipmentId === null || !state.item) return { kind: 'overview' };
  const index = queue.findIndex(
    (item) => String(item.shipmentId) === state.shipmentId
  );
  return {
    kind: 'shipment',
    item: index >= 0 ? queue[index] : state.item,
    position: index + 1,
    total: queue.length,
  };
};
